import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { Alert, Button, Form, Placeholder, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { crmPostventaV2Api } from "../../crmpostventa-v2/crmPostventaV2Api";
import ZocoCheckAlertsActionsMenu from "./ZocoCheckAlertsActionsMenu";
import {
  buildAttemptLabel,
  buildFilterOptions,
  buildWhatsAppUrl,
  DEFAULT_ZOCO_CHECK_PAGE_SIZE,
  filterZocoCheckRows,
  formatDate,
  isPastDate,
  mapStateLabel,
  normalizeText,
  normalizeZocoCheckItem,
  slicePage,
  ZOCO_CHECK_PAGE_SIZE_OPTIONS,
} from "./zocoCheckAlertsUtils";
import "./zoco-check-alerts.css";

const SEARCH_DEBOUNCE_MS = 300;
const FETCH_PAGE_SIZE = 200;
const MOBILE_BREAKPOINT = 900;

const emptyFilters = {
  search: "",
  priority: "",
  province: "",
  responsible: "",
  status: "",
  attempt: "",
};

const createGenerationSummary = (response, estimatedVisible) => [
  response?.message || "Sin mensaje.",
  Number.isFinite(response?.elegibles) ? `Elegibles: ${response.elegibles}` : null,
  Number.isFinite(response?.crear) ? `A crear en esta corrida: ${response.crear}` : null,
  Number.isFinite(response?.pendientesPorLimite) ? `Pendientes: ${response.pendientesPorLimite}` : null,
  estimatedVisible > 0 ? `Estimación visual con filtros actuales: ${estimatedVisible}` : null,
]
  .filter(Boolean)
  .join("\n");

const buildEmptyMessage = (hasFilters, customEmptyMessage) =>
  hasFilters
    ? "No se encontraron seguimientos con los filtros seleccionados."
    : customEmptyMessage || "No hay seguimientos Zoco Check pendientes.";

export default function ZocoCheckAlertsSection({
  processCode,
  processSlug,
  title,
  globalFilters,
  emptyMessage,
  onManage,
  onAssign,
  onRelease,
  onTake,
  onRefreshSummary,
  onMutationNonce = 0,
}) {
  const [filters, setFilters] = useState(emptyFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_ZOCO_CHECK_PAGE_SIZE);
  const [state, setState] = useState({
    loading: true,
    rows: [],
    total: 0,
    error: "",
    permissions: null,
  });
  const [rowBusy, setRowBusy] = useState({});
  const [detailsCache, setDetailsCache] = useState({});
  const [generateBusy, setGenerateBusy] = useState(false);
  const [viewportMobile, setViewportMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const scrollRef = useRef(null);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((value) => Boolean(normalizeText(value))),
    [filters]
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  useEffect(() => {
    const onResize = () => setViewportMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters.priority, filters.province, filters.responsible, filters.status, filters.attempt]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [page, pageSize]);

  const loadRows = async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let pageNumber = 1;
      let mergedRows = [];
      let total = 0;

      while (pageNumber === 1 || mergedRows.length < total) {
        const response = await crmPostventaV2Api.obtenerAlertas({
          procesoCodigo: processCode,
          fechaReferencia: globalFilters?.fechaReferencia,
          prioridad: globalFilters?.prioridad || undefined,
          estado: globalFilters?.estado || undefined,
          texto: globalFilters?.texto || undefined,
          pagina: pageNumber,
          tamanoPagina: FETCH_PAGE_SIZE,
        });

        const currentRows = Array.isArray(response?.filas) ? response.filas : [];
        total = Number(response?.total || currentRows.length || 0);
        mergedRows = mergedRows.concat(currentRows);

        if (!currentRows.length || mergedRows.length >= total) {
          setState({
            loading: false,
            rows: mergedRows.map((item) => normalizeZocoCheckItem({ ...item, procesoSlug: processSlug })),
            total,
            permissions: response?.permisos || null,
            error: "",
          });
          return;
        }

        pageNumber += 1;
      }
    } catch (error) {
      setState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: "No pudimos cargar los seguimientos Zoco Check.",
      });
    }
  };

  useEffect(() => {
    loadRows();
  }, [globalFilters?.estado, globalFilters?.fechaReferencia, globalFilters?.prioridad, globalFilters?.texto, processCode, processSlug]);

  useEffect(() => {
    if (onMutationNonce > 0) {
      loadRows();
    }
  }, [onMutationNonce]);

  const filteredRows = useMemo(
    () => filterZocoCheckRows(state.rows, { ...filters, search: debouncedSearch }),
    [debouncedSearch, filters, state.rows]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => slicePage(filteredRows, currentPage, pageSize), [currentPage, filteredRows, pageSize]);

  const priorityOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.priority), [state.rows]);
  const provinceOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.province), [state.rows]);
  const responsibleOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.responsible), [state.rows]);
  const statusOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.statusLabel), [state.rows]);

  const canGenerate = false;

  const readDetail = async (item) => {
    const cacheKey = String(item.id);
    if (detailsCache[cacheKey]) {
      return detailsCache[cacheKey];
    }

    const detail = await crmPostventaV2Api.obtenerDetalle(item.processSlug || processSlug, item.id);
    setDetailsCache((prev) => ({ ...prev, [cacheKey]: detail }));
    return detail;
  };

  useEffect(() => {
    const missingRows = pageRows.filter((item) => !detailsCache[String(item.id)]);
    if (!missingRows.length) {
      return;
    }

    Promise.allSettled(missingRows.map((item) => readDetail(item))).catch(() => null);
  }, [pageRows]);

  const handleResultChange = async (item, nextResult) => {
    if (!nextResult) {
      return;
    }

    setRowBusy((prev) => ({ ...prev, [item.id]: "result" }));
    try {
      await readDetail(item);
      await onManage?.(item, { initialResult: nextResult });
    } catch (error) {
      const message = String(error?.message || "");
      if (message.includes("403")) {
        Swal.fire(title, "No tenés permisos para modificar este seguimiento.", "warning");
      } else if (message.includes("409")) {
        Swal.fire(title, "Este seguimiento fue actualizado por otro usuario. Actualizamos la información para que puedas continuar.", "info");
        await loadRows();
      } else {
        Swal.fire(title, message || "No se pudo preparar la gestión.", "error");
      }
    } finally {
      setRowBusy((prev) => ({ ...prev, [item.id]: "" }));
    }
  };

  const handleCopyMessage = async (message) => {
    try {
      await navigator.clipboard.writeText(message || "");
      Swal.fire(title, "El mensaje sugerido quedó copiado.", "success");
    } catch {
      Swal.fire(title, "No se pudo copiar el mensaje sugerido.", "error");
    }
  };

  const handleGenerate = async () => {
    setGenerateBusy(true);
    try {
      const preview = await crmPostventaV2Api.generar(processSlug, {
        dryRun: true,
        fecha: globalFilters?.fechaReferencia,
      });

      const confirmation = await Swal.fire({
        title: "Generar Zoco Check",
        text: createGenerationSummary(preview, filteredRows.length),
        html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${createGenerationSummary(preview, filteredRows.length)}</pre><p style="margin-top:16px;">Se generarán los seguimientos Zoco Check correspondientes a los comercios que cumplen las condiciones actuales. Esta acción puede crear nuevas gestiones. ¿Deseás continuar?</p>`,
        icon: preview?.permiteEjecucionReal === false ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: "Generar Zoco Check",
        cancelButtonText: "Cancelar",
      });

      if (!confirmation.isConfirmed || preview?.permiteEjecucionReal === false) {
        return;
      }

      const response = await crmPostventaV2Api.generar(processSlug, {
        dryRun: false,
        fecha: globalFilters?.fechaReferencia,
      });

      await Swal.fire(title, response?.message || "Generación completada.", "success");
      await loadRows();
      await onRefreshSummary?.();
    } catch (error) {
      Swal.fire(title, error?.message || "No se pudo generar Zoco Check.", "error");
    } finally {
      setGenerateBusy(false);
    }
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      ...emptyFilters,
      search: "",
      priority: "",
      province: "",
      responsible: "",
      status: "",
      attempt: "",
    }));
    setPage(1);
  };

  const renderRowActions = (item) => {
    const whatsAppUrl = buildWhatsAppUrl(item.phone, item.suggestedMessage);
    const isBusy = Boolean(rowBusy[item.id]);

    return (
      <div className="zoco-check-alerts__row-actions">
        <Button
          type="button"
          size="sm"
          variant="light"
          className="zoco-check-alerts__details-btn"
          onClick={() => onManage?.(item)}
          disabled={isBusy}
        >
          Ver detalles
        </Button>
        <Button
          type="button"
          size="sm"
          className="zoco-check-alerts__whatsapp-btn"
          href={whatsAppUrl || undefined}
          target={whatsAppUrl ? "_blank" : undefined}
          rel={whatsAppUrl ? "noopener noreferrer" : undefined}
          disabled={!whatsAppUrl || isBusy}
          aria-label={whatsAppUrl ? `Abrir WhatsApp para ${item.commerceName}` : "El comercio no tiene teléfono cargado"}
          title={whatsAppUrl ? "Abrir WhatsApp" : "El comercio no tiene teléfono cargado"}
        >
          <FontAwesomeIcon icon={faWhatsapp} />
          <span>Whatsapp</span>
        </Button>
        <ZocoCheckAlertsActionsMenu
          item={item}
          disabled={isBusy}
          onTake={onTake}
          onAssign={onAssign}
          onRelease={onRelease}
          onManage={onManage}
          onCopyMessage={handleCopyMessage}
        />
      </div>
    );
  };

  const renderDesktopTable = () => (
    <div className="zoco-check-alerts__table-shell">
      <div className="zoco-check-alerts__table-scroll" ref={scrollRef}>
        <Table className="zoco-check-alerts__table mb-0">
          <thead>
            <tr>
              <th>Comercio</th>
              <th>CUIT</th>
              <th>Anticipo</th>
              <th>Intento</th>
              <th>Estado</th>
              <th>Resultado</th>
              <th>Próx. contacto</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((item) => {
              const cachedDetail = detailsCache[String(item.id)];
              const resultOptions = Array.isArray(cachedDetail?.instancia?.resultadosPermitidos)
                ? cachedDetail.instancia.resultadosPermitidos
                : [];

              return (
                <tr key={item.id}>
                  <td>
                    <button type="button" className="zoco-check-alerts__commerce-link" onClick={() => onManage?.(item)}>
                      {item.commerceName}
                    </button>
                  </td>
                  <td>{item.cuit}</td>
                  <td>
                    <span className={`zoco-check-alerts__advance ${item.advanceActive.toLowerCase() === "activo" ? "is-active" : "is-inactive"}`}>
                      {item.advanceActive.toLowerCase() === "activo" ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td>{buildAttemptLabel(item.attempt, item.maxAttempts)}</td>
                  <td>
                    <Form.Select
                      size="sm"
                      className="zoco-check-alerts__mini-select"
                      value={item.statusLabel}
                      disabled
                      aria-label={`Estado actual de ${item.commerceName}`}
                      title="La transición de estado se resuelve desde la gestión existente."
                    >
                      <option>{item.statusLabel}</option>
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      className="zoco-check-alerts__mini-select"
                      value=""
                      onFocus={() => readDetail(item).catch(() => null)}
                      onChange={(event) => handleResultChange(item, event.target.value)}
                      disabled={item.isReadOnly || rowBusy[item.id] === "result"}
                      aria-label={`Seleccionar resultado para ${item.commerceName}`}
                    >
                      <option value="">Seleccionar</option>
                      {resultOptions.map((option) => (
                        <option key={`${item.id}-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                    {rowBusy[item.id] === "result" ? <Spinner animation="border" size="sm" className="zoco-check-alerts__inline-spinner" /> : null}
                  </td>
                  <td>
                    <span className={`zoco-check-alerts__next-contact ${isPastDate(item.nextContactAt) ? "is-overdue" : ""}`}>
                      {formatDate(item.nextContactAt)}
                    </span>
                  </td>
                  <td>{renderRowActions(item)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderMobileCards = () => (
    <div className="zoco-check-alerts__cards">
      {pageRows.map((item) => {
        const cachedDetail = detailsCache[String(item.id)];
        const resultOptions = Array.isArray(cachedDetail?.instancia?.resultadosPermitidos)
          ? cachedDetail.instancia.resultadosPermitidos
          : [];

        return (
          <article key={item.id} className="zoco-check-alerts__card">
            <div className="zoco-check-alerts__card-head">
              <strong>{item.commerceName}</strong>
              <span>{item.cuit}</span>
            </div>
            <div className="zoco-check-alerts__card-grid">
              <div>
                <span>Anticipo</span>
                <strong>
                  <span className={`zoco-check-alerts__advance ${item.advanceActive.toLowerCase() === "activo" ? "is-active" : "is-inactive"}`}>
                    {item.advanceActive.toLowerCase() === "activo" ? "Activo" : "Inactivo"}
                  </span>
                </strong>
              </div>
              <div>
                <span>Intento</span>
                <strong>{buildAttemptLabel(item.attempt, item.maxAttempts)}</strong>
              </div>
              <div>
                <span>Estado</span>
                <strong>{item.statusLabel}</strong>
              </div>
              <div>
                <span>Próx. contacto</span>
                <strong>{formatDate(item.nextContactAt)}</strong>
              </div>
              <div>
                <span>Resultado</span>
                <Form.Select
                  size="sm"
                  className="zoco-check-alerts__mini-select"
                  value=""
                  onFocus={() => readDetail(item).catch(() => null)}
                  onChange={(event) => handleResultChange(item, event.target.value)}
                  disabled={item.isReadOnly || rowBusy[item.id] === "result"}
                >
                  <option value="">Seleccionar</option>
                  {resultOptions.map((option) => (
                    <option key={`${item.id}-mobile-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            {renderRowActions(item)}
          </article>
        );
      })}
    </div>
  );

  return (
    <section className="zoco-check-alerts-section">
      <div className="zoco-check-alerts__header">
        <h3>{title}</h3>
        <span>| Mostrando {pageRows.length} de {filteredRows.length}</span>
      </div>

      <div className="zoco-check-alerts__filters">
        <div className="zoco-check-alerts__search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <Form.Control
            type="search"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Comercio, CUIT, n.º..."
            aria-label="Buscar seguimientos Zoco Check"
          />
        </div>
        <Form.Select value={filters.priority} onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))} aria-label="Filtrar por prioridad">
          <option value="">Prioridad</option>
          {priorityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </Form.Select>
        {provinceOptions.length ? (
          <Form.Select value={filters.province} onChange={(event) => setFilters((prev) => ({ ...prev, province: event.target.value }))} aria-label="Filtrar por provincia">
            <option value="">Provincia</option>
            {provinceOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </Form.Select>
        ) : null}
        <Form.Select value={filters.responsible} onChange={(event) => setFilters((prev) => ({ ...prev, responsible: event.target.value }))} aria-label="Filtrar por responsable">
          <option value="">Responsable</option>
          {responsibleOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </Form.Select>
        <Form.Select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} aria-label="Filtrar por estado">
          <option value="">Estado</option>
          {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </Form.Select>
        <Form.Select value={filters.attempt} onChange={(event) => setFilters((prev) => ({ ...prev, attempt: event.target.value }))} aria-label="Filtrar por intento">
          <option value="">Intento</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
        </Form.Select>
      </div>

      {state.loading ? (
        <div className="zoco-check-alerts__skeleton">
          <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
          <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
          <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
        </div>
      ) : state.error ? (
        <Alert variant="warning" className="zoco-check-alerts__error">
          <div>{state.error}</div>
          <Button variant="outline-secondary" size="sm" onClick={loadRows}>
            Reintentar
          </Button>
        </Alert>
      ) : !filteredRows.length ? (
        <Alert variant="secondary" className="mb-0">
          {buildEmptyMessage(hasActiveFilters, emptyMessage)}
        </Alert>
      ) : viewportMobile ? (
        renderMobileCards()
      ) : (
        renderDesktopTable()
      )}

      <div className="zoco-check-alerts__footer">
        <div className="zoco-check-alerts__page-size">
          <Form.Select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} aria-label="Cantidad por página">
            {ZOCO_CHECK_PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} por página
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="zoco-check-alerts__pagination">
          {totalPages > 1 ? (
            <button type="button" className="zoco-check-alerts__page-nav" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>
              Anterior
            </button>
          ) : null}
          {Array.from({ length: totalPages }, (_, index) => index + 1).slice(0, 10).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={`zoco-check-alerts__page-btn ${pageNumber === currentPage ? "is-active" : ""}`}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}
          {totalPages > 1 ? (
            <button
              type="button"
              className="zoco-check-alerts__page-nav"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          ) : null}
        </div>

        <div className="zoco-check-alerts__footer-actions">
          <Button variant="light" onClick={resetFilters} disabled={!hasActiveFilters}>
            Limpiar filtros
          </Button>
          {canGenerate ? (
            <Button variant="success" onClick={handleGenerate} disabled={generateBusy}>
              {generateBusy ? "Generando..." : "Generar Zoco Check"}
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
